import { Injectable } from '@angular/core';
import { Control, ControlType } from '../../models/controls/control';
import { SelectControl } from '../../models/controls/select-control';
import { Option } from '../../models/controls/option';
import { TextControl } from '../../models/controls/text-control';
import { EmailControl } from '../../models/controls/email-control';
import { RadioControl } from '../../models/controls/radio-control';
import { PasswordControl } from '../../models/controls/password-control';
import { DateControl } from '../../models/controls/date-control';
import { CheckboxControl } from '../../models/controls/checkbox-control';
import { AhvControl } from '../../models/controls/ahv-control';
import { PhoneControl } from "../../models/controls/phone-control";

export interface ControlDefinition {
  name: string;
  placeholder: string;
  type:
    | 'text'
    | 'radio'
    | 'select'
    | 'email'
    | 'password'
    | 'date'
    | 'checkbox'
    | 'age'
    | 'ahv'
    | 'phone';
  options?: Option[];
  require?: string;
  required?: boolean;
  min?: number;
}

@Injectable({
  providedIn: 'root',
})
export class ControlFactoryService {
  constructor() {}

  createControls(controlDefinitions: ControlDefinition[]): Control<any>[] {
    return controlDefinitions.map((controlDefinition) => {
      const control = this.createControl(controlDefinition);
      if ('required' in controlDefinition) {
        control.required = controlDefinition.required === true;
      }
      if (controlDefinition.require) {
        control.require = controlDefinition.require;
      }
      if (!isNaN(controlDefinition.min as number)) {
        control.min = controlDefinition.min;
      }
      return control;
    });
  }

  private createControl(controlDefinition: ControlDefinition): Control<any> {
    switch (controlDefinition.type) {
      case 'select':
        return new SelectControl(
          controlDefinition.name,
          controlDefinition.placeholder,
          this.createOptions(controlDefinition.options as Option[])
        );

      case 'radio':
        return new RadioControl(
          controlDefinition.name,
          this.createOptions(controlDefinition.options as Option[])
        );

      case 'email':
        return new EmailControl(
          controlDefinition.name,
          controlDefinition.placeholder
        );

      case 'password':
        return new PasswordControl(
          controlDefinition.name,
          controlDefinition.placeholder
        );

      case 'date':
        return new DateControl(
          controlDefinition.name,
          controlDefinition.placeholder,
          ControlType.date
        );

      case 'age':
        return new DateControl(
          controlDefinition.name,
          controlDefinition.placeholder,
          ControlType.age
        );

      case 'ahv':
        return new AhvControl(
          controlDefinition.name,
          controlDefinition.placeholder
        );

      case 'phone':
        return new PhoneControl(
          controlDefinition.name,
          controlDefinition.placeholder
        );

      case 'checkbox':
        return new CheckboxControl(
          controlDefinition.name,
          controlDefinition.placeholder
        );

      default:
        return new TextControl(
          controlDefinition.name,
          controlDefinition.placeholder
        );
    }
  }

  private createOptions(options: Option[]): Option[] {
    options = options.map(
      (option) => new Option(option.value, option.text, option.position)
    );
    options.sort((optionA: Option, optionB: Option) => {
      const positionA = !isNaN(optionA.position as number)
        ? (optionA.position as number)
        : -1;
      const positionB = !isNaN(optionB.position as number)
        ? (optionB.position as number)
        : -1;
      if (positionA === -1 && positionB === -1) {
        return optionA.text.localeCompare(optionB.text);
      } else if (positionA !== -1 && positionB !== -1) {
        return positionA > positionB ? 1 : positionA < positionB ? -1 : 0;
      } else if (positionA === -1 && positionB !== -1) {
        return 1;
      }
      return -1;
    });
    return options;
  }
}
